import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Empty, Select, Spin } from "antd";
import debounce from "lodash/debounce";
import { type ApiRotaGetSearchParams, apiRotaGetSearch } from "pages/rota/api/apiRotaGetSearch";
import type { ApiRota } from "pages/rota/types/ApiRota";
import { useMemo, useState } from "react";

const SELECT_ALL_VALUE = -999999;

type SelectRotaMultipleProps = {
	value?: number[];
	selectedRotas?: ApiRota[];
	onChange?: (value: number[], rotas: ApiRota[]) => void;
	placeholder?: string;
	selectAllLabel?: string;
};

function getRotaLabel(rota: ApiRota): string {
	return rota.rot_identificacao || `Rota ${rota.rot_idpk}`;
}

export function SelectRotaMultiple({
	value = [],
	selectedRotas = [],
	onChange,
	placeholder,
	selectAllLabel,
}: SelectRotaMultipleProps) {
	const [searchValue, setSearchValue] = useState("");

	const params: ApiRotaGetSearchParams = useMemo(
		() => ({
			descricao: searchValue,
		}),
		[searchValue],
	);

	const { data, isFetching } = useQuery({
		queryKey: ["apiRotaGetSearch", "multiple", params],
		queryFn: () => apiRotaGetSearch({ params }),
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000,
	});

	const handleSearch = useMemo(() => debounce((nextValue: string) => setSearchValue(nextValue), 400), []);

	const options = useMemo(() => {
		const mapped = new Map<number, ApiRota>();

		for (const rota of selectedRotas) {
			mapped.set(rota.rot_idpk, rota);
		}

		for (const rota of data || []) {
			mapped.set(rota.rot_idpk, rota);
		}

		const mappedOptions = Array.from(mapped.values()).map((rota) => ({
			value: rota.rot_idpk,
			label: getRotaLabel(rota),
		}));

		if (!selectAllLabel) {
			return mappedOptions;
		}

		return [{ value: SELECT_ALL_VALUE, label: selectAllLabel }, ...mappedOptions];
	}, [data, selectAllLabel, selectedRotas]);

	const handleChange = (ids: number[]): void => {
		let selectedIds = ids;

		if (selectAllLabel) {
			const withoutAll = ids.filter((id) => id !== SELECT_ALL_VALUE);
			const clickedAll = ids.includes(SELECT_ALL_VALUE);
			const wasShowingAll = value.length === 0;

			if (ids.length === 0 || (clickedAll && !wasShowingAll) || (clickedAll && withoutAll.length === 0)) {
				setSearchValue("");
				onChange?.([], []);
				return;
			}

			selectedIds = withoutAll;
		}

		const searchResults = data || [];
		const nextSelected = selectedIds.map(
			(id) =>
				searchResults.find((rota) => rota.rot_idpk === id) ||
				selectedRotas.find((rota) => rota.rot_idpk === id) ||
				({ rot_idpk: id, rot_identificacao: `Rota ${id}` } as ApiRota),
		);

		onChange?.(selectedIds, nextSelected);
	};

	return (
		<Select
			mode="multiple"
			allowClear={true}
			showSearch={true}
			maxTagCount="responsive"
			placeholder={placeholder || "Selecione uma ou mais rotas"}
			value={selectAllLabel && value.length === 0 ? [SELECT_ALL_VALUE] : value}
			options={options}
			filterOption={false}
			onSearch={handleSearch}
			onChange={handleChange}
			notFoundContent={isFetching ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
			style={{ width: "100%" }}
		/>
	);
}
